// HowItWorks.jsx - The process in full, for the person who read the front page
// and wants the detail before they hand over anything.
//
// Written to answer the unspoken question at every stage, which is always some
// version of "and what happens if that goes wrong". Each stage carries its own
// drawing, so the page reads as a sequence rather than as six paragraphs.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, howToSchema, faqSchema } from "../lib/seo.jsx";
import { ALL_FAQS } from "./faqData.js";
import { PageHead, TrustStrip, CtaBand, FaqList, MobileCta, Figure } from "./parts.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { MailerKit, TruckRoute, VideoRecord, Loupe, PayoutEnvelope } from "../ui/illustrations.jsx";
import { KIT } from "../lib/media.js";
import { ArrowRight, Shield, Truck, Clock, Dollar, Video, Scale } from "../ui/icons.jsx";

////////// THE STAGES //////////
// Each one names what we do, what you do, and the thing that could go wrong.

const STAGES = [
  {
    icon: Truck,
    art: MailerKit,
    title: "You ask for a kit",
    body: "Fill in the short form or call us. Tell us roughly what you have, which only helps us pick the right insurance level and the right size mailer. A person reads every request, usually the same day.",
    detail: "The kit goes out by first class mail in a plain padded envelope. Nothing on the outside says what it is or who it is from.",
    worry: ["What if I change my mind before it arrives?", "Nothing happens. There is no charge and no follow-up beyond a single message asking whether the kit arrived."],
  },
  {
    icon: Shield,
    art: null,
    title: "The kit arrives",
    body: "Inside is a padded mailer and a prepaid FedEx 2Day return label, already addressed and already insured. There is nothing to print, nothing to pay, and no forms to complete.",
    detail: "The standard label carries $1,000 of insurance. If what you are sending is worth more, reply to any of our messages before you ship and we will send a label with higher coverage.",
    worry: ["What if the kit never turns up?", "Tell us and we send another. It happens a handful of times a year and it costs you nothing."],
  },
  {
    icon: Scale,
    art: TruckRoute,
    title: "You send your items",
    body: "Put everything in, seal it, and hand it to a FedEx counter or drop it in a FedEx box. Keep the receipt. You get a text the moment it scans into the network.",
    detail: "Insurance starts at that first scan. This is exactly why we ask you to hand it over rather than leave it out for collection.",
    worry: ["What if it goes missing in transit?", "It is insured from the first scan. Keep your drop-off receipt, tell us, and we handle the claim with the carrier."],
  },
  {
    icon: Video,
    art: VideoRecord,
    title: "We open it on camera",
    body: "Every package is recorded from sealed envelope to scale, before anything is touched. The video lands on your tracking page the same day.",
    detail: "We then weigh each piece, deduct stones and non-metal parts, and test the purity by acid or XRF. Every one of those numbers goes on your page.",
    worry: ["What if you say something was not in there?", "That is precisely what the video is for. It runs unbroken from the seal being cut."],
  },
  {
    icon: Clock,
    art: Loupe,
    title: "You get an itemised offer",
    body: "Piece by piece, with the gross weight, the stone deduction, the metal content in grams and the amount for each. No single lump sum, no trust us.",
    detail: "The offer is held at that price for 48 hours, because metal prices move. Take longer and we requote at the current price, free, as many times as you need.",
    worry: ["What if I think it is wrong?", "Reply and say so. If a weight or a purity looks off we will reweigh and refilm it while you watch."],
  },
  {
    icon: Dollar,
    art: PayoutEnvelope,
    title: "You accept, or you do not",
    body: "Accept and choose how to be paid. Before 3pm Eastern on a business day, the transfer goes out the same day. Decline and everything comes back, insured, at our cost.",
    detail: "Declining costs nothing and always has. There is no restocking fee, no return postage, and no phone call trying to talk you round.",
    worry: ["What if I accept and regret it?", "Call us straight away. Once a payment has left we cannot unwind it, but until then we will."],
  },
];

const RELEVANT_FAQS = ALL_FAQS.filter((item) =>
  ["What does the kit cost?", "Is my package insured?", "What if the package is lost?", "How much can I send in one kit?", "How do I get paid?", "What if I change my mind after I ship?"].includes(item.question)
);

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How selling gold by mail works"
        path="/how-it-works"
        description="Step by step: request a free insured kit, mail your items, watch us open and weigh them on camera, then accept or decline an itemised offer. Free insured return either way."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "How it works", to: "/how-it-works" }])} />
      <JsonLd data={howToSchema({
        name: "How to sell gold, silver or platinum by mail",
        description: "The full process, from requesting a free insured kit to getting paid.",
        path: "/how-it-works",
        steps: STAGES.map((stage) => ({ title: stage.title, text: stage.body })),
      })} />
      <JsonLd data={faqSchema(RELEVANT_FAQS)} />

      <PageHead
        eyebrow="The whole process"
        title="Six stages, and what happens if any of them go wrong."
        lede="Most people have money in their account within a week. Here is every step, including the parts other companies do not put in writing."
        art={() => (
          <Figure
            src={KIT.spread}
            alt="Mailers and booklets, fanned out"
            sizes="(min-width: 1024px) 26vw, 60vw"
            className="max-w-[17rem] ml-auto"
            priority
          />
        )}
      >
        <Link to="/get-a-kit" className="btn btn-primary btn-lg">Get a free kit <ArrowRight size={17} /></Link>
      </PageHead>

      <TrustStrip className="mb-2" />

      <div className="shell band">
        <ol className="space-y-12 md:space-y-20">
          {STAGES.map((stage, index) => {
            const Glyph = stage.icon;
            const Drawing = stage.art;
            const flipped = index % 2 === 1;

            return (
              <li key={stage.title} className="grid gap-5 md:gap-8 lg:gap-12 lg:grid-cols-[64px_minmax(0,1fr)_300px] items-start">
                <Reveal from="none" className="flex lg:block items-center gap-4">
                  <span className="numeral">{String(index + 1).padStart(2, "0")}</span>
                  <Glyph size={22} className="text-bronze lg:mt-5" />
                </Reveal>

                <Reveal delay={80} className="min-w-0">
                  <h2 className="display display-md text-ink">{stage.title}</h2>
                  <p className="mt-3 text-[15.5px] sm:text-[16px] leading-relaxed text-ink-soft measure">{stage.body}</p>
                  <p className="hint mt-3 leading-relaxed measure">{stage.detail}</p>

                  {/* The drawing sits under the copy on a phone, where a third
                      column would squeeze both to nothing. */}
                  {Drawing && (
                    <div className="lg:hidden mt-6 max-w-[19rem]">
                      <Drawing />
                    </div>
                  )}

                  <aside className="lg:hidden mt-6 border-l-2 border-bronze-soft pl-4">
                    <p className="text-[14px] font-semibold text-ink">{stage.worry[0]}</p>
                    <p className="hint mt-1.5 leading-relaxed">{stage.worry[1]}</p>
                  </aside>
                </Reveal>

                <Reveal from={flipped ? "left" : "right"} delay={140} className="hidden lg:block self-start">
                  {Drawing && <Drawing className="mb-6 max-w-[17rem]" />}
                  <aside className="border-l-2 border-bronze-soft pl-5">
                    <p className="text-[14px] font-semibold text-ink">{stage.worry[0]}</p>
                    <p className="hint mt-1.5 leading-relaxed">{stage.worry[1]}</p>
                  </aside>
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>

      {/* The kit, drawn. */}
      <section className="border-y border-rule bg-surface/40 band clip-x">
        <div className="shell grid gap-9 lg:grid-cols-2 lg:gap-16 lg:items-center">
          <Reveal>
            <p className="eyebrow mb-3">What arrives</p>
            <h2 className="display display-lg text-ink">A padded envelope. That is it.</h2>
            <StaggerBoxed as="ul" step={80} className="mt-6 space-y-3">
              {[
                "A padded mailer sized for jewellery, not a box you have to find tape for.",
                "A prepaid FedEx 2Day label, already addressed to us, already insured.",
                "A single card with your order number and the phone number of a person.",
                "Nothing on the outside that says what it is.",
              ].map((line) => (
                <li key={line} className="flex gap-3 text-[15px] text-ink-soft">
                  <span className="w-4 h-px bg-bronze-soft mt-3 shrink-0" />
                  {line}
                </li>
              ))}
            </StaggerBoxed>
          </Reveal>

          <Reveal from="right" delay={120}>
            <Figure
              photo="kit"
              fallback={MailerKit}
              alt="The mailer that arrives, printed and ready to post back"
              className="max-w-md lg:ml-auto"
            />
          </Reveal>
        </div>
      </section>

      <section className="shell band">
        <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow mb-3">Still wondering</p>
            <h2 className="display display-lg text-ink">The practical questions.</h2>
            <Link to="/faq" className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-ink underline-grow">
              All questions <ArrowRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <FaqList items={RELEVANT_FAQS} />
          </Reveal>
        </div>
      </section>

      <CtaBand />
      <MobileCta />
    </>
  );
}
